/* eslint-disable import/no-default-export, import/order, max-lines, no-nested-ternary, react/no-unescaped-entities, react/prop-types */
import { generateUUID } from "common/utils/uuid";

import {
  DEFAULT_VALUES,
  GANG_SHEET_HEIGHT_ERROR,
  GANG_SHEET_MIN_HEIGHT_INCHES,
  GANG_SHEET_MIN_HEIGHT_PX,
  GANG_SHEET_REQUIRED_WIDTH_INCHES,
  GANG_SHEET_REQUIRED_WIDTH_PX,
  GANG_SHEET_WIDTH_ERROR,
  MIN_STICKER_SIZE
} from "./common/constants";
import ErrorTypes from "./error-types";

export function getFormPayload() {
  const $form = document.getElementById("product-details-quantity-form") as HTMLFormElement | null;
  if (!$form) {
    throw new Error("product-details-quantity-form not found in DOM");
  }
  const $orderNumber = document.querySelector(".js-order-number-container") as HTMLElement | null;

  const formPayload: { event_id: string; product_id: string; order_number?: string } = {
    event_id: ($form.elements.namedItem("event_id") as HTMLInputElement | null)?.value ?? "",
    product_id: ($form.elements.namedItem("product_id") as HTMLInputElement | null)?.value ?? ""
  };

  let orderNumber: string | undefined;
  const numberElement = $form.elements.namedItem("number");
  if (numberElement) {
    if ("length" in numberElement && (numberElement as RadioNodeList).length) {
      orderNumber = (numberElement as RadioNodeList)[0]
        ? ((numberElement as RadioNodeList)[0] as HTMLInputElement).value
        : undefined;
    } else {
      orderNumber = (numberElement as HTMLInputElement).value;
    }
  } else if ($orderNumber) {
    orderNumber = $orderNumber.dataset["orderNumber"];
  }
  if (orderNumber) {
    formPayload.order_number = orderNumber;
  }

  return formPayload;
}

export function getIsBuyItAgainStatus() {
  return !!document.getElementById("js-buy-it-again-modal");
}

export function isArtworkWithError(artwork: {
  metadata?: { errors?: unknown[] } | null;
  errorMessage?: string | null;
  uploadError?: string | null;
  retryFailed?: boolean;
  imageTooSmall?: boolean;
  [key: string]: unknown;
}) {
  return (
    (artwork.metadata?.errors?.length ?? 0) > 0 ||
    !!artwork.errorMessage ||
    !!artwork.uploadError ||
    artwork.retryFailed ||
    !!artwork.imageTooSmall
  );
}

// Narrower than isArtworkWithError — excludes imageTooSmall so resolution-gate
// failures persist until the user explicitly dismisses them via ImageTooSmallOverlay's
// "Got It" button. Other error types (uploadError, retryFailed, metadata errors) have
// no dismiss UI and are expected to auto-clear when the user uploads a replacement.
export function isPurgeableUploadError(artwork: {
  metadata?: { errors?: unknown[] } | null;
  errorMessage?: string | null;
  uploadError?: string | null;
  retryFailed?: boolean;
  imageTooSmall?: boolean;
  [key: string]: unknown;
}) {
  return isArtworkWithError(artwork) && !artwork.imageTooSmall;
}

export function shouldUseEnhancedMetadata(
  artwork: {
    enhanceImage: boolean;
    enhancementTier: string;
  } | null
) {
  if (!artwork) return false;
  return (
    artwork.enhanceImage &&
    artwork.enhancementTier !== "custom" &&
    artwork.enhancementTier !== "print_original"
  );
}

/**
 * Gets the appropriate artwork source URL based on availability priority
 * @param {Object} artwork - The artwork object
 * @returns {string} The artwork source URL
 */
export function getArtworkSrc(
  artwork: {
    url?: string;
    originalImageUrl?: string;
    enhancedWithBgUrl?: string;
    sliderPreviewUrl?: string;
  } | null
) {
  return (
    artwork?.url ||
    artwork?.originalImageUrl ||
    artwork?.enhancedWithBgUrl ||
    artwork?.sliderPreviewUrl ||
    ""
  );
}

export function toggleGlobalDtfError(value: boolean) {
  const $container = document.querySelector(".js-product-details-dtf-sheet");
  $container?.classList?.toggle("product-details-dtf-sheet--with-error", Boolean(value));
}

function hexToRgb(hex: string) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);

  return r >= 0 && r <= 255 && g >= 0 && g <= 255 && b >= 0 && b <= 255 ? { r, g, b } : null;
}

function toBase64(file: Blob): Promise<string | ArrayBuffer | null> {
  return new Promise<string | ArrayBuffer | null>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function loadImage(url: string | ArrayBuffer | null): Promise<HTMLImageElement> {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = "Anonymous";
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("Unable to load the image"));
    image.src = String(url);
  });
}

function loadImages(urls: string[]): Promise<HTMLImageElement[]> {
  return Promise.all(urls.map(loadImage));
}

export async function convertImageToHexColor(fileOrUrl: string | Blob, hexColor: string) {
  const src = typeof fileOrUrl === "string" ? fileOrUrl : await toBase64(fileOrUrl);
  const image = await loadImage(src);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  canvas.width = image.naturalWidth;
  canvas.height = image.naturalHeight;

  if (!ctx) {
    throw new Error("Canvas context unavailable");
  }

  ctx.drawImage(image, 0, 0);

  const rgb = hexToRgb(hexColor);
  if (!rgb) {
    throw new Error("Invalid hex color");
  }

  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const { data } = imageData;

  for (let i = 0; i < data.length; i += 4) {
    data[i] = rgb.r;
    data[i + 1] = rgb.g;
    data[i + 2] = rgb.b;
  }

  ctx.putImageData(imageData, 0, 0);

  const base64Image = canvas.toDataURL("image/png");
  return base64Image;
}

export async function mergeImages({ urls, bgColor }: { urls: string[]; bgColor: string }) {
  const rgb = hexToRgb(bgColor);
  if (!rgb) {
    throw new Error("Invalid hex color");
  }

  const images = await loadImages(urls);
  const width = Math.max(...images.map(image => image.width));
  const height = Math.max(...images.map(image => image.height));

  const canvas = document.createElement("canvas");

  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext("2d");

  if (!ctx) {
    throw new Error("Canvas context unavailable");
  }

  for (let i = 0; i < images.length; i += 1) {
    const image = images[i]!;
    const xPosition = (width - image.width) / 2;
    const yPosition = (height - image.height) / 2;
    ctx.drawImage(image, xPosition, yPosition);

    if (i === 0) {
      const imageData = ctx.getImageData(0, 0, width, height);
      const { data } = imageData;

      for (let j = 0; j < data.length; j += 4) {
        data[j] = rgb.r;
        data[j + 1] = rgb.g;
        data[j + 2] = rgb.b;
      }

      ctx.putImageData(imageData, 0, 0);
    }
  }

  return canvas.toDataURL("image/png");
}

export function getPdpImage() {
  return (document.querySelector(".js-carousel-image") as HTMLImageElement | null)?.src;
}

export function getContrastingColor(hexColor: string) {
  // Remove the hash at the start if it's there
  hexColor = hexColor.replace(/^#/, "");

  // Parse r, g, b values
  let r = parseInt(hexColor.substring(0, 2), 16) / 255;
  let g = parseInt(hexColor.substring(2, 4), 16) / 255;
  let b = parseInt(hexColor.substring(4, 6), 16) / 255;

  // Convert to a relative luminance value
  r = r <= 0.03928 ? r / 12.92 : ((r + 0.055) / 1.055) ** 2.4;
  g = g <= 0.03928 ? g / 12.92 : ((g + 0.055) / 1.055) ** 2.4;
  b = b <= 0.03928 ? b / 12.92 : ((b + 0.055) / 1.055) ** 2.4;

  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;

  // Determine contrasting color
  const newColor = luminance > 0.179 ? "#000000" : "#FFFFFF";

  return { color: newColor };
}

export function groupByKeyPrefix(
  data: Record<string, unknown>,
  splitBy = "-"
): Record<string, Array<Record<string, unknown>>> {
  const groupedResult: Record<string, Array<Record<string, unknown>>> = {};

  Object.keys(data)
    .sort()
    .forEach(key => {
      const [prefix, id] = key.split(splitBy);
      if (prefix === undefined) return;
      if (!groupedResult[prefix]) {
        groupedResult[prefix] = [];
      }
      groupedResult[prefix].push({ ...(data[key] as Record<string, unknown>), id });
    });

  return groupedResult;
}

export function zipArray(
  array1: Record<string, unknown>[],
  array2: Record<string, unknown>[]
): Record<string, unknown>[] {
  return array1
    .slice(0, Math.min(array1.length, array2.length))
    .map((item: Record<string, unknown>, index: number) => ({
      ...item,
      ...array2[index]
    }));
}

export function zipArrayByKey(
  array1: Record<string, unknown>[],
  array2: Record<string, unknown>[],
  key: string
): Record<string, unknown>[] {
  const lookupMap = new Map(array2.map((item: Record<string, unknown>) => [item[key], item]));

  return array1.map((item1: Record<string, unknown>) => {
    const item2 = lookupMap.get(item1[key]);
    return item2 ? { ...item1, ...(item2 as Record<string, unknown>) } : item1;
  });
}

export function getTitleCaseString(title: string) {
  return title.replace(/-/g, " ").replace(/\b\w/g, (match: string) => match.toUpperCase());
}

export function scaleToFit(
  bounds: [number | string, number | string],
  dimensions: [number | string, number | string]
) {
  const maxWidth = Number(bounds[0]);
  const maxHeight = Number(bounds[1]);
  const originalWidth = Number(dimensions[0]);
  const originalHeight = Number(dimensions[1]);

  if (!maxWidth || !maxHeight) {
    throw new Error("Invalid max dimensions provided");
  }

  const scale = Math.min(maxWidth / originalWidth, maxHeight / originalHeight);

  return {
    width: (originalWidth * scale).toFixed(2),
    height: (originalHeight * scale).toFixed(2)
  };
}

export function scaleToFitRatio(
  maxWidth: number | string,
  maxHeight: number | string,
  aspectRatio: number
) {
  maxWidth = Number(maxWidth);
  maxHeight = Number(maxHeight);

  if (!maxWidth || !maxHeight) {
    throw new Error("Invalid dimensions provided");
  }

  const heightFromWidth = maxWidth / aspectRatio;

  if (heightFromWidth <= maxHeight) {
    return {
      width: maxWidth.toFixed(2),
      height: heightFromWidth.toFixed(2)
    };
  }

  const widthFromHeight = maxHeight * aspectRatio;

  return {
    width: widthFromHeight.toFixed(2),
    height: maxHeight.toFixed(2)
  };
}

export function getRandomId() {
  return generateUUID();
}

export const calculateDimensions = (aspectRatio: number, width: number, isSticker: boolean) => {
  const factor = 1 / aspectRatio;
  const newWidth = isSticker && width < MIN_STICKER_SIZE ? MIN_STICKER_SIZE : width;
  const newHeight = Math.round(newWidth * factor * 100) / 100;
  return {
    width: newWidth,
    height: newHeight
  };
};

export const calculateDefaultDimensions = ({
  aspectRatio,
  width = 1,
  isUntouched = true
}: {
  aspectRatio: number;
  width?: number;
  isUntouched?: boolean;
}) => {
  if (!aspectRatio) {
    return { width: 1, height: 1 };
  }

  const factor = 1 / aspectRatio;

  if (!isUntouched) {
    return {
      width,
      height: Math.round(width * factor * 100) / 100
    };
  }

  const isWidthSmaller = Math.sqrt(1 / factor) < Math.sqrt(1 / factor) * factor;

  return {
    width: Math.round((isWidthSmaller ? 1 : 1 / factor) * 100) / 100,
    height: Math.round((isWidthSmaller ? factor : 1) * 100) / 100
  };
};

export const recalculateCustomSizes = (
  sizesAndQuantities: Array<{
    preset: string;
    width: number;
    height: number;
    [key: string]: unknown;
  }>,
  aspectRatio: number
) =>
  sizesAndQuantities.map(
    (item: { preset: string; width: number; height: number; [key: string]: unknown }) => {
      if (item.preset === "custom") {
        const isUntouched =
          Number(item.width) === DEFAULT_VALUES.width &&
          Number(item.height) === DEFAULT_VALUES.height;
        return {
          ...item,
          ...calculateDefaultDimensions({ aspectRatio, width: item.width, isUntouched })
        };
      }
      return item;
    }
  );

export const INCOMPATIBLE_FILE_FORMATS = ["heic", "heif", "svg", "tiff"];

export function isIncompatibleFileFormat(contentType: string | null | undefined) {
  if (!contentType) {
    return false;
  }
  const incompatibleFormatsRegex = new RegExp(INCOMPATIBLE_FILE_FORMATS.join("|"), "i");

  return incompatibleFormatsRegex.test(contentType);
}

/**
 * Validates gang sheet dimensions according to requirements:
 * - Width must be exactly 22" (6600px at 300 DPI)
 * - Height must be at least 24" (7200px at 300 DPI)
 * @param {Object} metadata - Artwork metadata object
 * @returns {string[]} Array of error codes, empty if validation passes
 */
export function validateGangSheetDimensions(
  metadata: {
    width?: number;
    widthInch?: number;
    height?: number;
    heightInch?: number;
  } | null
) {
  if (!metadata) {
    return [];
  }

  const errors: string[] = [];

  const widthPx = metadata.width;
  const { widthInch } = metadata;

  const isNonNullish = (value: number | undefined) => value != null;

  if (isNonNullish(widthPx) && widthPx !== GANG_SHEET_REQUIRED_WIDTH_PX) {
    errors.push(GANG_SHEET_WIDTH_ERROR);
  } else if (isNonNullish(widthInch) && widthInch !== GANG_SHEET_REQUIRED_WIDTH_INCHES) {
    errors.push(GANG_SHEET_WIDTH_ERROR);
  }

  const heightPx = metadata.height;
  const { heightInch } = metadata;

  if (isNonNullish(heightPx) && heightPx < GANG_SHEET_MIN_HEIGHT_PX) {
    errors.push(GANG_SHEET_HEIGHT_ERROR);
  } else if (isNonNullish(heightInch) && heightInch < GANG_SHEET_MIN_HEIGHT_INCHES) {
    errors.push(GANG_SHEET_HEIGHT_ERROR);
  }

  return errors;
}

/**
 * Checks if an artwork has gang sheet validation errors
 * @param {Object} artwork - Artwork object
 * @returns {boolean} True if artwork has gang sheet errors
 */
export function hasGangSheetError(
  artwork: {
    metadata?: { errors?: string[] } | null;
    errorMessage?: string | null;
    errorType?: string | null;
  } | null
) {
  if (!artwork) return false;

  const { metadata, errorMessage, errorType } = artwork;

  // Check metadata errors array — destructure default only fires for undefined, so
  // `metadata` may still be null when the server serializes it that way.
  if (
    metadata?.errors?.some(
      (error: string) => error === GANG_SHEET_WIDTH_ERROR || error === GANG_SHEET_HEIGHT_ERROR
    )
  ) {
    return true;
  }

  // Check errorType
  if (errorType?.toString() === ErrorTypes.INVALID_GANG_SHEET) {
    return true;
  }

  // Check errorMessage strings
  if (errorMessage === GANG_SHEET_WIDTH_ERROR || errorMessage === GANG_SHEET_HEIGHT_ERROR) {
    return true;
  }

  return false;
}
